import React, { useState, useEffect, useRef } from 'react';
import { Container, Badge, Button, Spinner, Nav } from 'react-bootstrap';
import styles from './Style/SellerFAQ.module.css';
import api from '../../seller-apis/api';
import Pagination from '../common/PaginationComponent/index';

// Import Open Sans font
const fontImportStyle = `
  @import url('https://fonts.googleapis.com/css2?family=Open+Sans:wght@300;400;500;600;700&display=swap');
`;

const SellerFAQ = () => {
  const [faqs, setFaqs] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedDescriptions, setExpandedDescriptions] = useState({});
  const [needsReadMore, setNeedsReadMore] = useState({}); // NEW: Track which items actually need "Read more"
  const [activeCategory, setActiveCategory] = useState('all');
  const [currPage, setCurrentPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const itemsPerPage = 5;
  const contentRefs = useRef({});

  useEffect(() => {
    // Add Open Sans font to document head
    const styleElement = document.createElement('style');
    styleElement.innerHTML = fontImportStyle;
    document.head.appendChild(styleElement);

    // Clean up function
    return () => {
      document.head.removeChild(styleElement);
    };
  }, []);

  useEffect(() => {
    // Fetch categories on mount
    fetchCategories();
  }, []);

  useEffect(() => {
    // Fetch FAQs when category or page changes
    fetchFAQs();
  }, [activeCategory, currPage]);

  // FIX #1: Check actual rendered height to determine if "Read more" is needed
  useEffect(() => {
    if (faqs.length > 0) {
      // Small delay to ensure DOM is rendered
      const timer = setTimeout(() => {
        const readMoreNeeded = {};
        Object.keys(contentRefs.current).forEach(key => {
          const element = contentRefs.current[key];
          if (element) {
            // Compare actual content height vs visible height
            const scrollHeight = element.scrollHeight;
            const clientHeight = element.clientHeight;
            // Only show "Read more" if content is actually truncated (with 5px buffer)
            readMoreNeeded[key] = scrollHeight > clientHeight + 5;
          }
        });
        setNeedsReadMore(readMoreNeeded);
      }, 100);
      
      return () => clearTimeout(timer);
    }
  }, [faqs]);

  // Apply Quill styles programmatically after render
  useEffect(() => {
    const applyQuillStyles = () => {
      // Large text size
      document.querySelectorAll('.ql-size-large').forEach(el => {
        el.style.setProperty('font-size', '18px', 'important');
      });
      
      // Small text size
      document.querySelectorAll('.ql-size-small').forEach(el => {
        el.style.setProperty('font-size', '12px', 'important');
      });
      
      // Huge text size
      document.querySelectorAll('.ql-size-huge').forEach(el => {
        el.style.setProperty('font-size', '32px', 'important');
      });
    };
    
    applyQuillStyles();
    
    // Set up MutationObserver for dynamic content
    const observer = new MutationObserver(mutations => {
      mutations.forEach(mutation => {
        if (mutation.addedNodes.length) {
          applyQuillStyles();
        }
      });
    });
    
    observer.observe(document.body, { 
      childList: true, 
      subtree: true 
    });
    
    return () => {
      observer.disconnect();
    };
  }, [faqs]);

  // Function to clean up only problematic attributes (like Google Docs IDs)
  // but PRESERVE user-defined styles (colors, backgrounds, etc.)
  const cleanupHTML = (html) => {
    if (!html) return '';
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;
    
    // Only remove problematic attributes, NOT styles
    const allElements = tempDiv.querySelectorAll('*');
    allElements.forEach(el => {
      // Remove only Google Docs internal IDs
      if (el.hasAttribute('id') && el.getAttribute('id').startsWith('docs-internal-guid')) {
        el.removeAttribute('id');
      }
    });
    
    return tempDiv.innerHTML;
  };

  // Function to fetch categories
  const fetchCategories = async () => {
    try {
      const response = await api.faq.getCategories();
      
      if (response && response.data && response.data.success) {
        setCategories(response.data.data || []);
      }
    } catch (err) {
      console.error('Error fetching FAQ categories:', err);
    }
  };

  // Function to fetch FAQs
  const fetchFAQs = async () => {
    try {
      setLoading(true);
      
      const payload = {
        page: currPage,
        limit: itemsPerPage,
        status: 'Active'
      };

      // Add category filter if not "all"
      if (activeCategory !== 'all') {
        payload.category = activeCategory;
      }

      const response = await api.faq.getVendorFAQs(payload);
      
      if (response && response.data && response.data.success) {
        setFaqs(response.data.data || []);
        setTotalRecords(response.data.pagination?.total || response.data.count || 0);
      } else {
        setFaqs([]);
        setTotalRecords(0);
      }
    } catch (err) {
      console.error('Error fetching FAQs:', err);
      setError("Error fetching FAQs. Please try again later.");
      setFaqs([]);
      setTotalRecords(0);
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCategoryChange = (categoryId) => {
    setActiveCategory(categoryId);
    setCurrentPage(1);
    setExpandedDescriptions({});
    setNeedsReadMore({}); // Reset read more state
  };

  const toggleDescription = (faqId, type, e) => {
    // Don't prevent default if clicking on an actual link
    if (e.target.tagName === 'A') {
      return; // Let the link work normally
    }
    e.preventDefault();
    setExpandedDescriptions(prev => ({
      ...prev,
      [`${faqId}-${type}`]: !prev[`${faqId}-${type}`]
    }));
  };

  const handleContentClick = (e) => {
    // Check if the clicked element or any of its parents is an anchor tag
    let target = e.target;
    
    // Traverse up to find if we clicked on or inside an anchor tag
    while (target && target !== e.currentTarget) {
      if (target.tagName === 'A') {
        const href = target.getAttribute('href');
        
        if (href) {
          e.preventDefault();
          e.stopPropagation();
          
          if (href.startsWith('mailto:')) {
            // Open email client
            window.location.href = href;
          } else if (href.startsWith('http://') || href.startsWith('https://')) {
            // Open external links in new tab
            window.open(href, '_blank', 'noopener,noreferrer');
          } else {
            // For other links
            window.location.href = href;
          }
          return;
        }
      }
      target = target.parentElement;
    }
  };

  // Decode HTML entities to actual HTML
  const decodeHTML = (html) => {
    if (!html) return '';
    const txt = document.createElement('textarea');
    txt.innerHTML = html;
    return txt.value;
  };

  // Extract plain text from HTML to check length
  const extractPlainText = (html) => {
    if (!html) return '';
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;
    return tempDiv.textContent || tempDiv.innerText || '';
  };

  const renderContent = (content, faqId, type, isQuestion = false) => {
    // First decode HTML entities
    const decodedContent = decodeHTML(content);
    
    // Clean up HTML but PRESERVE user-defined styles (colors, backgrounds)
    const processedContent = cleanupHTML(decodedContent);
    
    const plainText = extractPlainText(decodedContent);
    
    // Questions always show full content without "Read more"
    if (isQuestion) {
      return (
        <div 
          className={styles.faqQuestion}
          dangerouslySetInnerHTML={{ __html: processedContent }}
          onClick={handleContentClick}
        />
      );
    }
    
    // Answers have "Read more" functionality using CSS-based truncation
    const isExpanded = expandedDescriptions[`${faqId}-${type}`];
    const refKey = `${faqId}-${type}`;
    
    // FIX #1: Use actual height comparison instead of character count
    const maxLength = 250;
    const mightNeedTruncation = plainText.length > maxLength;
    
    // If content is short, don't add truncation logic at all
    if (!mightNeedTruncation) {
      return (
        <div 
          className={styles.faqAnswer}
          dangerouslySetInnerHTML={{ __html: processedContent }}
          onClick={handleContentClick}
        />
      );
    }
    
    // For potentially long content, use ref to measure actual height
    const showReadMore = needsReadMore[refKey] !== false; // Show by default until measured
    const actuallyNeedsReadMore = needsReadMore[refKey] === true;
    
    return (
      <div className={styles.answerContainer}>
        <div 
          ref={el => contentRefs.current[refKey] = el}
          className={`${styles.faqAnswer} ${isExpanded ? styles.expanded : styles.collapsed}`}
          dangerouslySetInnerHTML={{ __html: processedContent }}
          onClick={handleContentClick}
        />
        {/* FIX #1 & #2: Only show "Read more" if content is actually truncated */}
        {!isExpanded && actuallyNeedsReadMore && (
          <Button 
            variant="link"
            className={styles.readMoreButton}
            onClick={(e) => toggleDescription(faqId, type, e)}
          >
            Read more
          </Button>
        )}
        {isExpanded && actuallyNeedsReadMore && (
          <Button 
            variant="link"
            className={styles.readMoreButton}
            onClick={(e) => toggleDescription(faqId, type, e)}
          >
            Read less
          </Button>
        )}
      </div>
    );
  };

  // Loading state
  if (loading && currPage === 1) {
    return (
      <Container fluid className={styles.pageContainer}>
        <div className={styles.faqHeader}>
          <h1 className={styles.faqTitle}>Frequently Asked Questions</h1>
          <Spinner animation="border" role="status" size="sm">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
        </div>
        <div className="text-center p-4">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
          <p className="mt-2">Loading FAQs...</p>
        </div>
      </Container>
    );
  }

  return (
    <Container fluid className={styles.pageContainer}>
      {/* Header section */}
      <div className={styles.faqHeader}>
        <h1 className={styles.faqTitle}>Frequently Asked Questions</h1>
        <Badge 
          pill 
          bg="dark" 
          style={{ 
            backgroundColor: '#165b5b',
            fontSize: '14px',
            padding: '4px 12px'
          }}
        >
          {totalRecords} FAQs
        </Badge>
      </div>

      {/* Category Tabs */}
      <div className={styles.categoryTabs}>
        <Nav variant="tabs" className={styles.navTabs}>
          <Nav.Item>
            <Nav.Link 
              active={activeCategory === 'all'}
              onClick={() => handleCategoryChange('all')}
              className={activeCategory === 'all' ? styles.activeTab : styles.inactiveTab}
            >
              All Categories
            </Nav.Link>
          </Nav.Item>
          {categories.map((category) => (
            <Nav.Item key={category._id}>
              <Nav.Link 
                active={activeCategory === category._id}
                onClick={() => handleCategoryChange(category._id)}
                className={activeCategory === category._id ? styles.activeTab : styles.inactiveTab}
              >
                {category.name}
              </Nav.Link>
            </Nav.Item>
          ))}
        </Nav>
      </div>

      {/* Error state */}
      {error && (
        <div className="alert alert-danger mt-3">{error}</div>
      )}

      {/* Main content - Full width FAQ list */}
      <div className={styles.mainContent}>
        <div className={styles.contentWrapper}>
          {/* FAQ List - Full Width */}
          <div className={styles.faqListSection}>
            {loading ? (
              <div className="text-center p-4">
                <Spinner animation="border" role="status" />
              </div>
            ) : faqs.length === 0 ? (
              <div className="text-center p-4 bg-white rounded shadow-sm">
                No FAQs available in this category
              </div>
            ) : (
              faqs.map((faq, index) => (
                <div key={faq._id} className={styles.faqItem}>
                  {/* Question with number */}
                  <div className={styles.faqQuestionWrapper}>
                    <span className={styles.faqNumber}>
                      {(currPage - 1) * itemsPerPage + index + 1}.
                    </span>
                    {renderContent(faq.question, faq._id, 'question', true)}
                  </div>

                  {/* Answer */}
                  <div className={styles.faqAnswerWrapper}>
                    {renderContent(faq.answer, faq._id, 'answer', false)}
                  </div>
                </div>
              ))
            )}

            {/* Pagination */}
            {totalRecords > itemsPerPage && (
              <div className="pn-pagination-wrapper mt-4">
                <Pagination
                  currentPage={currPage}
                  totalRecords={totalRecords}
                  itemsPerRow={itemsPerPage}
                  handlePageChange={handlePageChange}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </Container>
  );
};

export default SellerFAQ;